import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { HitReportDataDTO, HitReportIngredientDTO } from 'business/models/Models';
import { getHeader, TABLE_LAYOUT } from './ExperimentReportPDF';
import { MIME_TYPES } from 'utils/MimeTypes';
import { createPdfWithPopupBlockerHandler } from './utils';
import moment from 'moment';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

type DropData = {
  expName: string;
  dateImaged: Date;
  title: string;
  sampleVol: number;
  wellVol: number;
  proteinName: string;
  score: string;
  wellIngs: string[];
  captures: CapturesData[];
};
type CapturesData = {
  id: number;
  img: string;
  ext: string;
};

export type HitsReportOptions = {
  latestInspectionOnly?: boolean;
  uvHitsOnly?: boolean;
};
export function createHitReportPDF(
  reportData: HitReportDataDTO[],
  saveReport: boolean,
  options?: HitsReportOptions,
  onPopupBlocked?: () => void,
) {
  const pdfDefinition = {
    info: {
      title: 'Hits Report',
      author: 'Rock Maker',
      subject: 'Hits Report',
    },
    content: CreateContent(reportData, options),
    styles: {
      tableStyle: { alignment: 'center' },
      tableHeader: { lineHeight: 1, bold: true, fontSize: '12', fillColor: '#D9D9D9' },
    },
    defaultStyle: {
      fontSize: 10,
      lineHeight: 1.5,
    },
    pageMargins: [50, 70, 50, 30],
    header: getHeader(),
    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    'Hits Report',
    saveReport,
    onPopupBlocked,
  );
}
function CreateContent(reportData: HitReportDataDTO[], options?: HitsReportOptions) {
  const content = [];
  content.push({ text: 'Hits Report', fontSize: '28', alignment: 'center' });
  content.push({ text: 'Hits By Ingredients', fontSize: '20', bold: true });
  [
    options?.latestInspectionOnly && 'Hits from latest inspection only',
    options?.uvHitsOnly && 'UV Hits only',
  ]
    .filter(Boolean)
    .forEach((text) => {
      content.push({
        text,
        fontSize: '14',
        bold: false,
      });
    });

  printHitIngredientsTable(content, reportData);

  reportData.forEach((exp) => {
    const experimentNamesDict: { [key: number]: string } = {};
    const plateImagedDateDict: { [key: number]: string } = {};
    const captureProfilesDict: { [key: number]: string } = {};
    const plateBarcodeDict: { [key: number]: string } = {};
    const dropsReport: {
      [key: number]: { [key: number]: { [key: number]: { [key: number]: DropData } } };
    } = {};

    for (let i = 0; i < exp.dropsData.length; i++) {
      const drop = exp.dropsData[i];

      if (!dropsReport[drop.experimentId]) {
        dropsReport[drop.experimentId] = {};
      }
      if (!dropsReport[drop.experimentId][drop.plateId]) {
        dropsReport[drop.experimentId][drop.plateId] = {};
      }
      if (!dropsReport[drop.experimentId][drop.plateId][drop.inspectionId]) {
        dropsReport[drop.experimentId][drop.plateId][drop.inspectionId] = {};
      }
      if (
        !dropsReport[drop.experimentId][drop.plateId][drop.inspectionId][drop.wellDropId]
      ) {
        dropsReport[drop.experimentId][drop.plateId][drop.inspectionId][drop.wellDropId] =
          {
            title: drop.wellName + '-' + drop.dropNumber,
            sampleVol: drop.sampleVolume,
            wellVol: drop.dropVolume,
            score: drop.wellDropScoreName,
            wellIngs: drop.wellIngs,
            proteinName: drop.proteinName,
            captures: [
              {
                id: drop.captureProfileId,
                img: drop.imageData,
                ext: drop.imageExt,
              } as CapturesData,
            ],
          } as DropData;
      } else {
        if (
          dropsReport[drop.experimentId][drop.plateId][drop.inspectionId][
            drop.wellDropId
          ].captures.findIndex((c) => c.id == drop.captureProfileId) <= 0
        ) {
          dropsReport[drop.experimentId][drop.plateId][drop.inspectionId][
            drop.wellDropId
          ].captures.push({
            id: drop.captureProfileId,
            img: drop.imageData,
            ext: drop.imageExt,
          });
        }
      }
      if (!experimentNamesDict[drop.experimentId]) {
        experimentNamesDict[drop.experimentId] = drop.expName;
      }
      const key = `${drop.plateId}_${drop.inspectionId}`;
      if (!plateImagedDateDict[key]) {
        //plateImagedDateDict[drop.plateId] = new Date(drop.dateImaged).toLocaleDateString('en-US');
        plateImagedDateDict[key] = moment(drop.dateImaged).format('YYYY-MM-DD hh:mm a');
      }
      if (!captureProfilesDict[drop.captureProfileId]) {
        captureProfilesDict[drop.captureProfileId] = drop.captureProfileName;
      }
      if (!plateBarcodeDict[drop.plateId]) {
        plateBarcodeDict[drop.plateId] = drop.plateBarcode;
      }
    }

    getHItsByExperimentTable(
      dropsReport,
      content,
      experimentNamesDict,
      plateImagedDateDict,
      captureProfilesDict,
      plateBarcodeDict,
    );
  });
  return content;
}

function printHitIngredientsTable(content: any[], reportData: HitReportDataDTO[]) {
  const ingredients: any[] = [];

  reportData.forEach((exp) => {
    ingredients.push(...exp.ingredientsData);
  });

  const mergedIngs: { [key: number]: HitReportIngredientDTO } = {};
  ingredients.forEach((ing) => {
    if (mergedIngs[ing.id]) {
      mergedIngs[ing.id].ingredientCount += ing.ingredientCount;
      if (mergedIngs[ing.id].highConcentration < ing.highConcentration) {
        mergedIngs[ing.id].highConcentration = ing.highConcentration;
      }
      if (mergedIngs[ing.id].lowConcentration > ing.lowConcentration) {
        mergedIngs[ing.id].lowConcentration = ing.lowConcentration;
      }
      if (mergedIngs[ing.id].highPh < ing.highPh) {
        mergedIngs[ing.id].highPh = ing.highPh;
      }
      if (mergedIngs[ing.id].lowPh > ing.lowPh) {
        mergedIngs[ing.id].lowPh = ing.lowPh;
      }
    } else {
      mergedIngs[ing.id] = ing;
    }
  });
  content.push(getHitIngredientsTable(mergedIngs));
}

function getHItsByExperimentTable(
  dropsData: {
    [key: number]: { [key: number]: { [key: number]: { [key: number]: DropData } } };
  },
  content: any[],
  expNames,
  imagedDates,
  captureProfiles,
  plateBarcodes,
) {
  Object.keys(dropsData).forEach((expId) => {
    content.push({
      text: 'Experiment Name: ' + expNames[expId],
      fontSize: 14,
      bold: true,
      pageBreak: 'before',
    });
    let plateCount = 1;
    Object.keys(dropsData[expId]).forEach((plateId) => {
      const plateDateImaged =
        imagedDates[
          Object.keys(imagedDates)
            .filter((k) => k.split('_')[0] === String(plateId))
            .sort((a, b) => Number(b.split('_')[1]) - Number(a.split('_')[1]))[0]
        ];
      const plateBarcode = plateBarcodes[plateId];
      content.push({
        text: 'Barcode (ID): ' + plateBarcode + ' (' + plateId + ')',
        fontSize: 12,
      });
      content.push({ text: 'Imaged: ' + plateDateImaged, fontSize: 12 });

      const tableBody = [];
      const headerRow = [{ text: 'Drop Info', width: '50%', style: 'tableHeader' }];
      Object.keys(captureProfiles).forEach((cp) =>
        headerRow.push({ text: captureProfiles[cp], width: '25%', style: 'tableHeader' }),
      );
      tableBody.push(headerRow);

      Object.keys(dropsData[expId][plateId])
        .sort((a, b) => parseInt(b) - parseInt(a))
        .forEach((inspectionId) => {
          Object.keys(dropsData[expId][plateId][inspectionId]).forEach((dropId) => {
            const dateImaged = imagedDates[`${plateId}_${inspectionId}`];
            const drop: DropData = dropsData[expId][plateId][inspectionId][dropId];
            const tableRow: any[] = [
              {
                stack: [
                  { text: drop.title, style: 'header2', margin: [0, 5, 0, 5] },
                  {
                    text: [{ text: 'Sample: ', bold: true }, { text: drop.proteinName }],
                  },
                  {
                    text: [
                      { text: 'Sample / Well Volume: ', bold: true },
                      drop.sampleVol + 'uL' + ' / ' + drop.wellVol + 'uL',
                    ],
                  },
                  { text: 'Scores: ' + drop.score, bold: true },
                  drop.wellIngs ? drop.wellIngs.map((x) => x) : '',
                ],
              },
            ];

            Object.keys(captureProfiles).forEach((cp) => {
              const dropCapture = drop.captures.find((d) => d.id === Number(cp));
              if (dropCapture && dropCapture?.img) {
                tableRow.push({
                  stack: [
                    {
                      image:
                        'data:' +
                        MIME_TYPES[dropCapture.ext] +
                        ';base64,' +
                        dropCapture.img,
                      height: 130,
                      width: 160,
                    },
                    {
                      text: `*${dateImaged}`,
                      alignment: 'center',
                    },
                  ],
                });
              } else {
                tableRow.push({
                  text: 'No Image Found',
                  fontSize: 18,
                  alignment: 'center',
                  margin: [0, 55, 0, 0],
                });
              }
            });

            tableBody.push(tableRow);
          });
        });
      let pgBreak = 'after';
      if (plateCount == Object.keys(dropsData[expId]).length) {
        pgBreak = '';
      }
      content.push({
        table: { body: tableBody, headerRows: 1, dontBreakRows: true },
        pageBreak: pgBreak,
      });
      plateCount++;
    });
  });
  //content.push( {table:{ body: tableBody }} );
  // /* Construct table Body Detail */
  // let tableRow = [];
  // for (let index = 0; index < dropsData.length; index++) {
  //   let drop = dropsData[index];
  //   tableRow.push([
  //     {
  //       stack: [
  //         {text: drop.wellName + ' - ' + drop.dropNumber, style: "header2", margin: [0, 10, 0, 10]},
  //         {text: [
  //             {text: "Sample: ", bold: true},
  //             {text: drop.proteinName}
  //           ],
  //           margin: [0, 5, 0, 5]
  //         },
  //         {text:[
  //             {text: "Sample / Well Volume: ", bold: true},
  //             drop.dropVolume + " / " + drop.sampleVolume
  //             //dropsData[index].proteinVolume + " / " + data[index].wellVolume
  //           ],
  //           margin: [0, 5, 0, 5]
  //         },
  //         {text: "Scores: "+drop.wellDropScoreName, bold: true, margin: [0, 5, 0, 5]},
  //         drop.wellIngs.map( x => x )
  //         ]
  //     },
  //     {
  //       stack: [
  //         //"Image Data",
  //         {image: "data:image/jpeg;base64,"+drop.imageData, height: 120, width: 130},
  //         new Date(drop.dateImaged).toLocaleDateString("en-US")
  //       ]
  //     }
  //   ]);
  // }
  // tableBody.push(...tableRow);

  // return tableBody;
}

function getHitIngredientsTable(ingredientData: {
  [key: number]: HitReportIngredientDTO;
}) {
  const body: any = [
    [
      { text: 'Ingredient', bold: true, alignment: 'left', style: 'tableHeader' },
      { text: 'No. of Hits', bold: true, style: 'tableHeader' },
      { text: 'pH Range', bold: true, style: 'tableHeader' },
      { text: 'Concentration Range', bold: true, style: 'tableHeader' },
    ],
  ];
  Object.keys(ingredientData).forEach((i) => {
    const ing: HitReportIngredientDTO = ingredientData[i];
    body.push([
      { text: ing.name, alignment: 'left' },
      ing.ingredientCount,
      ing.isBuffer
        ? ing.lowPh == ing.highPh
          ? ing.lowPh
          : ing.lowPh + ' - ' + ing.highPh
        : '-',
      ing.lowConcentration == ing.highConcentration
        ? ing.lowConcentration.toFixed(3) + ' ' + ing.concentrationUnits
        : ing.lowConcentration.toFixed(3) +
          ' - ' +
          ing.highConcentration.toFixed(3) +
          ' ' +
          ing.concentrationUnits,
    ]);
  });

  return {
    style: 'tableStyle',
    table: {
      widths: ['40%', '15%', '15%', '25%'],
      headerRows: 1,
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}
